import React, { useEffect, useState } from 'react';
import {
  Box,
  Typography,
  CircularProgress,
  Snackbar,
  Alert,
  Tabs,
  Tab,
  Chip,
  IconButton,
  TextField,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText
} from '@mui/material';
import { Check, Close, Search, Edit as EditIcon, Save as SaveIcon } from '@mui/icons-material';
import { styled } from '@mui/system';
import axiosInstance from '../../api/axiosConfig';
import NoResultsImage from '../../assets/no-results.png';
import PageHeader from '../common/PageHeader';
import PageContainer from '../common/PageContainer';

// Styled Components
const StyledTab = styled(Tab)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 700,
  padding: theme.spacing(0.95, 2.2),
  '&.Mui-selected': {
    color: theme.palette.primary.main,
    backgroundColor: 'transparent',
    borderRadius: 0,
  },
}));

const RequestCard = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1.35, 0.2),
  borderRadius: 0,
  backgroundColor: 'transparent',
  borderBottom: `1px solid ${theme.palette.divider}`,
  marginBottom: theme.spacing(0.2),
  '&:hover': {
    backgroundColor: theme.palette.action.hover,
    transition: 'all 0.2s ease',
  },
}));

const StyledButton = styled(Button)(({ theme }) => ({
  textTransform: 'none',
  fontWeight: 600,
  padding: theme.spacing(0.82, 1.6),
  borderRadius: 0,
}));

const RegularizationRequestList = () => {
  const [requests, setRequests] = useState([]);
  const [filteredRequests, setFilteredRequests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [statusFilter, setStatusFilter] = useState('PENDING');
  const [search, setSearch] = useState('');
  const [editMode, setEditMode] = useState({});
  const [editedData, setEditedData] = useState({});
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [dialog, setDialog] = useState({ open: false, requestId: null, action: '' });

  const schoolId = localStorage.getItem('schoolId');

  useEffect(() => {
    const fetchRequests = async () => {
      setLoading(true);
      setError('');
      try {
        if (!schoolId) throw new Error('School ID not found in localStorage');
        const response = await axiosInstance.get(`/api/regularization-requests/school/${schoolId}`, {
          params: { status: statusFilter },
        });
        setRequests(response.data || []);
        setFilteredRequests(response.data || []);
      } catch (err) {
        setError(err.response?.data?.message || 'Failed to fetch requests');
        setRequests([]);
        setFilteredRequests([]);
      } finally {
        setLoading(false);
      }
    };
    fetchRequests();
  }, [statusFilter, schoolId]);

  const handleTabChange = (event, newValue) => {
    setStatusFilter(newValue);
    setSearch('');
    setEditMode({});
  };

  const handleSearchChange = e => {
    const value = e.target.value;
    setSearch(value);
    const filtered = requests.filter(req =>
      req.employeeId.toString().includes(value) ||
      (req.employeeName && req.employeeName.toLowerCase().includes(value.toLowerCase()))
    );
    setFilteredRequests(filtered);
  };

  const handleApproveReject = (requestId, action) => {
    setDialog({ open: true, requestId, action });
  };

  const handleDialogClose = () => {
    setDialog({ open: false, requestId: null, action: '' });
  };

  const handleDialogConfirm = async () => {
    const { requestId, action } = dialog;
    try {
      await axiosInstance.put(`/api/regularization-requests/${requestId}`, { status: action }, {
        headers: { schoolId },
      });
      setSnackbar({ open: true, message: `Request ${action.toLowerCase()}`, severity: 'success' });
      setRequests(prev =>
        prev.map(req => (req.requestId === requestId ? { ...req, status: action } : req))
      );
      setFilteredRequests(prev =>
        prev.map(req => (req.requestId === requestId ? { ...req, status: action } : req))
      );
      handleDialogClose();
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to update', severity: 'error' });
    }
  };

  const handleEditToggle = (requestId, request) => {
    setEditMode(prev => ({ ...prev, [requestId]: !prev[requestId] }));
    setEditedData(prev => ({
      ...prev,
      [requestId]: {
        reason: request.reason || '',
        checkinTime: request.checkinTime ? new Date(request.checkinTime).toISOString().slice(11, 16) : '',
        checkoutTime: request.checkoutTime ? new Date(request.checkoutTime).toISOString().slice(11, 16) : '',
      },
    }));
  };

  const handleEditChange = (requestId, field, value) => {
    setEditedData(prev => ({
      ...prev,
      [requestId]: { ...prev[requestId], [field]: value },
    }));
  };

  const handleSaveEdit = async requestId => {
    const edited = editedData[requestId];
    const request = requests.find(req => req.requestId === requestId);
    const payload = {
      reason: edited.reason,
      checkinTime: edited.checkinTime ? `${new Date(request.absenceDate).toISOString().slice(0, 10)}T${edited.checkinTime}:00` : null,
      checkoutTime: edited.checkoutTime ? `${new Date(request.absenceDate).toISOString().slice(0, 10)}T${edited.checkoutTime}:00` : null,
      status: request.status,
    };

    try {
      await axiosInstance.put(`/api/regularization-requests/${requestId}`, payload, {
        headers: { schoolId },
      });
      setRequests(prev =>
        prev.map(req => (req.requestId === requestId ? { ...req, ...payload } : req))
      );
      setFilteredRequests(prev =>
        prev.map(req => (req.requestId === requestId ? { ...req, ...payload } : req))
      );
      setEditMode(prev => ({ ...prev, [requestId]: false }));
      setSnackbar({ open: true, message: 'Request updated successfully', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err.response?.data?.message || 'Failed to update', severity: 'error' });
    }
  };

  const handleSnackbarClose = () => setSnackbar(prev => ({ ...prev, open: false }));

  const capitalize = str => str ? str.charAt(0).toUpperCase() + str.slice(1).toLowerCase() : '';
  const formatTime = timeStr => timeStr ? new Date(timeStr).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--:--';

  const renderRequestCard = request => {
    const isEditing = editMode[request.requestId] || false;
    const showActions = statusFilter === 'PENDING';

    return (
      <RequestCard key={request.requestId}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="body1" fontWeight="medium">
              {capitalize(request.employeeName)} (ID: {request.employeeId})
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {new Date(request.absenceDate).toLocaleDateString()}
            </Typography>
            {isEditing ? (
              <Box sx={{ mt: 1 }}>
                <TextField
                  label="Check-In"
                  type="time"
                  value={editedData[request.requestId]?.checkinTime || ''}
                  onChange={e => handleEditChange(request.requestId, 'checkinTime', e.target.value)}
                  size="small"
                  sx={{ mr: 1, width: '120px' }}
                />
                <TextField
                  label="Check-Out"
                  type="time"
                  value={editedData[request.requestId]?.checkoutTime || ''}
                  onChange={e => handleEditChange(request.requestId, 'checkoutTime', e.target.value)}
                  size="small"
                  sx={{ mr: 1, width: '120px' }}
                />
                <TextField
                  label="Reason"
                  value={editedData[request.requestId]?.reason || ''}
                  onChange={e => handleEditChange(request.requestId, 'reason', e.target.value)}
                  size="small"
                  fullWidth
                  sx={{ mt: 1 }}
                />
              </Box>
            ) : (
              <>
                <Typography variant="body2" color="text.secondary">
                  {formatTime(request.checkinTime)} - {formatTime(request.checkoutTime)}
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                  <strong>Reason:</strong> {request.reason || 'N/A'}
                </Typography>
              </>
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Chip
              label={capitalize(request.status)}
              color={request.status === 'APPROVED' ? 'success' : request.status === 'REJECTED' ? 'error' : 'warning'}
              size="small"
              variant="outlined"
            />
            {showActions && (
              <>
                {isEditing ? (
                  <IconButton
                    color="primary"
                    onClick={() => handleSaveEdit(request.requestId)}
                  >
                    <SaveIcon />
                  </IconButton>
                ) : (
                  <IconButton
                    color="primary"
                    onClick={() => handleEditToggle(request.requestId, request)}
                  >
                    <EditIcon />
                  </IconButton>
                )}
                <IconButton
                  color="success"
                  onClick={() => handleApproveReject(request.requestId, 'APPROVED')}
                >
                  <Check />
                </IconButton>
                <IconButton
                  color="error"
                  onClick={() => handleApproveReject(request.requestId, 'REJECTED')}
                >
                  <Close />
                </IconButton>
              </>
            )}
          </Box>
        </Box>
      </RequestCard>
    );
  };

  return (
    <PageContainer sx={{ width: '100%' }}>
      <PageHeader
        title="Regularization Requests"
        subtitle="Review attendance correction requests and take approval actions."
      />

      <Tabs
        value={statusFilter}
        onChange={handleTabChange}
        sx={{ mb: 1.5, borderBottom: '1px solid', borderColor: 'divider' }}
      >
        <StyledTab label="Pending" value="PENDING" />
        <StyledTab label="Approved" value="APPROVED" />
        <StyledTab label="Rejected" value="REJECTED" />
      </Tabs>

      <TextField
        placeholder="Search by ID or Name"
        value={search}
        onChange={handleSearchChange}
        size="small"
        sx={{ mb: 1.2, width: { xs: '100%', sm: 320 } }}
        InputProps={{
          startAdornment: <Search sx={{ mr: 1, color: 'text.secondary' }} />,
        }}
      />

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
          <CircularProgress />
        </Box>
      ) : error ? (
        <Typography variant="body1" color="error" sx={{ textAlign: 'center', mt: 4 }}>
          {error}
        </Typography>
      ) : filteredRequests.length === 0 ? (
        <Box sx={{ textAlign: 'center', mt: 4 }}>
          <img src={NoResultsImage} alt="No Results" style={{ width: '150px', opacity: 0.8 }} />
          <Typography variant="body1" color="text.secondary" sx={{ mt: 2 }}>
            No {statusFilter.toLowerCase()} regularization requests found.
          </Typography>
        </Box>
      ) : (
        <Box>{filteredRequests.map(renderRequestCard)}</Box>
      )}

      <Dialog open={dialog.open} onClose={handleDialogClose} maxWidth="xs" fullWidth>
        <DialogTitle sx={{ bgcolor: 'primary.main', color: '#fff', fontWeight: 'medium' }}>
          {dialog.action === 'APPROVED' ? 'Approve Request' : 'Reject Request'}
        </DialogTitle>
        <DialogContent sx={{ p: 3 }}>
          <DialogContentText variant="body1" color="text.secondary">
            Are you sure you want to {dialog.action.toLowerCase()} this regularization request?
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <StyledButton onClick={handleDialogClose} variant="outlined">
            Cancel
          </StyledButton>
          <StyledButton
            onClick={handleDialogConfirm}
            color={dialog.action === 'APPROVED' ? 'success' : 'error'}
            variant="contained"
          >
            {dialog.action}
          </StyledButton>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert severity={snackbar.severity} onClose={handleSnackbarClose}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default RegularizationRequestList;
